import Head from "next/head";
import Link from "next/link";
import { useEffect } from "react";
import NextScript from "next/script";
import { Link as MuiLink } from "@mui/material";

import { routes } from "src/shared/routes";
import { constants } from "src/shared/constants";
import { openPdfInNewTab } from "src/utils/utils";
import { global_config } from "src/shared/global_config";
import { useSSTheme } from "src/components/reusable/hooks/useSSTheme";
import { useRouterExt } from "src/components/reusable/hooks/useRouterExt";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "How do I claim my tickets?",
    answer:
      "You will receive a text when the window to opt in is opened. The check in window starts at 24 hours prior to tipoff. After checking in you will have the opportunity to purchase guest tickets & upgrades if they are available.",
  },
  {
    question: "Do I get a ticket to each game on the schedule?",
    answer:
      "Tickets are guaranteed for the games defined in the pass. There are no penalties for not attending a game, however you do need to confirm your attendance in order to receive a ticket. Please note that your Orlando Magic Slam Dunk Pass only guarantees you tickets to games you have checked in for.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "3 hours prior to game start you will receive a text confirming that tickets have been delivered to Ticketmaster Account Manager and the Orlando Magic app.",
  },
  {
    question: "Where will my seats be located?",
    answer: "Seats will be throughout the arena and mostly in the upper bowl.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer:
      "Yes, the Passes are non-transferable, but can be assigned to others in your party to enter separately.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        Once tickets have been sent to your account, they cannot be changed.
        Please contact{" "}
        <a href="mailto:help@orlandomagic.com">help@orlandomagic.com</a> for any
        assistance you may require.
      </>
    ),
  },
  {
    question: "Can I link tickets with other pass holders?",
    answer: (
      <>
        You can absolutely create a group to link with other pass holders!
        Navigate to the{" "}
        <Link
          href={{
            pathname: routes.my_group,
            query: { teamSchema: "orlandomagic_pass" },
          }}
        >
          My Group
        </Link>{" "}
        tab in your menu and copy the invite. This will ensure you are seated
        next to or close enough to hand a drink to one another for the game. You
        will need to ensure your group is created and all members have joined at
        least 4 hours prior to game start in order to be seated with one
        another.
      </>
    ),
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
];

export const TERMS_AND_CONDITION = () => {
  const theme = useSSTheme();

  return (
    <>
      I agree to the Orlando Magic&apos;s{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(
            "https://ss-leagues.s3.amazonaws.com/NBA/Orlando%20Magic%20Marketing%20Assets/2023-24%20Terms%20and%20Conditions%20-%20Fast%20Break.pdf"
          );
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms & Conditions
      </span>{" "}
      and{" "}
      <MuiLink
        target="_blank"
        href="https://www.nba.com/privacy-policy"
        sx={{
          fontWeight: "bold",
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          color: theme.colors.teamPrimary,
        }}
      >
        Privacy Policy
      </MuiLink>{" "}
      and Attend&apos;s{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.TERMS_AND_CONDITIONS_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Terms & Conditions
      </span>{" "}
      and{" "}
      <span
        onClick={(ev) => {
          ev?.preventDefault();
          ev.stopPropagation();
          openPdfInNewTab(global_config.PRIVACY_POLICY_PDF_URL);
        }}
        style={{
          cursor: "pointer",
          color: theme.colors.teamPrimary,
          textDecoration: "underline",
          textUnderlineOffset: "0.2em",
          fontWeight: "bold",
        }}
      >
        Privacy Policy
      </span>
      .
    </>
  );
};

export const AnalyticsHeader = () => {
  const router = useRouterExt();
  const { TEAM_SCHEMA } = useTeamConfig();

  useEffect(() => {
    const handleRouteChange = (path: string) => {
      try {
        const url = `${window.location.origin}/${path}`;
        if (window?.fbq) {
          window?.fbq("track", "PageView");
        }
        if (window?.gtag) {
          window?.gtag("event", "page_view", {
            page_location: url,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  return (
    <Head>
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-XTHMZHVGB4"
      />

      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'G-XTHMZHVGB4');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              const { cartRes, checkoutRes } = e.detail;
              gtag('event', 'purchase', {
                'send_to': 'G-XTHMZHVGB4',
                'value': cartRes.totalAmount,
                'currency': 'USD',
                'transaction_id': checkoutRes.orderNumber
              });
              console.log('gtag analytics purchase event fired.');
            });
        `,
        }}
      />
    </Head>
  );
};
